import React, { useState, useEffect, useCallback } from 'react';
import { 
    Search, 
    Loader2, 
    AlertCircle, 
    User, 
    MapPin, 
    Zap, 
    Tag, 
    FileText,
    Download,
    Image,
    File,
    ChevronDown,
    ChevronUp,
    X,
    FileSignature,
    CheckCircle2,
    XCircle
} from 'lucide-react';
import AppendixWrapper from './common/AppendixWrapper';
import { useAppendix } from '../context/AppendixContext';
import { useAuth } from '../context/AuthContext';

const toDisplayValue = (value) => {
    if (value === null || value === undefined || typeof value !== 'object') return value;
    if (Array.isArray(value)) return value.map(toDisplayValue);
    return value.name || value.label || value.value || value.id || '';
};

const normalizeLeadForDisplay = (lead) => Object.fromEntries(
    Object.entries(lead).map(([key, value]) => [
        key,
        key === 'attachments' || key === 'raw_data' ? value : toDisplayValue(value)
    ])
);

const LeadDeepSearchAppendix = () => {
    const { registerAppendix, leadSearchRequest } = useAppendix();
    const { authFetch } = useAuth();
    const id = 'lead-deep-search';
    
    const [searchType, setSearchType] = useState('phone'); // phone, email, cups
    const [searchValue, setSearchValue] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [results, setResults] = useState([]);
    const [expandedLeads, setExpandedLeads] = useState({});
    // Estado del segundo paso: campañas, attachments y contrato vinculado,
    // cargados en diferido por lead para no retrasar el pintado inicial.
    const [leadExtras, setLeadExtras] = useState({});

    useEffect(() => {
        registerAppendix(id, {
            title: 'Búsqueda de Leads',
            icon: Search,
            defaultPosition: { x: 300, y: 100 }
        });
    }, [registerAppendix]);

    // Validaciones
    const validatePhone = (phone) => {
        // Teléfono español: 6-9 seguido de 8 dígitos
        const phoneRegex = /^[6-9]\d{8}$/;
        return phoneRegex.test(phone.replace(/\s/g, ''));
    };

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validateCUPS = (cups) => {
        // CUPS español: ES + 16 dígitos + 2 letras
        const cupsRegex = /^ES\d{16}[A-Z]{2}$/i;
        return cupsRegex.test(cups.replace(/\s/g, ''));
    };

    const isValidInput = () => {
        if (!searchValue.trim()) return false;
        
        switch (searchType) {
            case 'phone':
                return validatePhone(searchValue);
            case 'email':
                return validateEmail(searchValue);
            case 'cups':
                return validateCUPS(searchValue);
            default:
                return false;
        }
    };

    // Segundo paso: campañas, attachments e info de contrato vinculado.
    // Se dispara por lead, en paralelo y sin bloquear el resultado ya pintado.
    const fetchLeadExtra = useCallback(async (lead) => {
        setLeadExtras(prev => ({
            ...prev,
            [lead.id]: { ...(prev[lead.id] || {}), loading: true, error: null }
        }));

        try {
            const params = new URLSearchParams();
            params.append('lead_id', lead.id);
            if (lead.phone) params.append('phone', lead.phone);
            if (lead.email) params.append('email', lead.email);
            if (lead.dni) params.append('dni', lead.dni);

            const response = await authFetch(`/api/zoho/deep-search/lead-extra?${params.toString()}`);
            const data = await response.json();

            if (response.ok && data.success) {
                setLeadExtras(prev => ({
                    ...prev,
                    [lead.id]: {
                        loading: false,
                        error: null,
                        campaigns: data.campaigns || [],
                        attachments: data.attachments || [],
                        contractInfo: data.contract_info || { has_contract: false, contracts: [] }
                    }
                }));
            } else {
                setLeadExtras(prev => ({
                    ...prev,
                    [lead.id]: { ...(prev[lead.id] || {}), loading: false, error: data.message || 'No se pudo cargar la info adicional' }
                }));
            }
        } catch (err) {
            console.error('Error cargando info adicional del lead:', err);
            setLeadExtras(prev => ({
                ...prev,
                [lead.id]: { ...(prev[lead.id] || {}), loading: false, error: 'Error al cargar campañas/contrato' }
            }));
        }
    }, [authFetch]);

    const runSearch = useCallback(async (type, value) => {
        setLoading(true);
        setError(null);
        setResults([]);
        setExpandedLeads({});
        setLeadExtras({});

        try {
            const params = new URLSearchParams();
            params.append(type, value.trim());

            const response = await authFetch(`/api/zoho/deep-search?${params.toString()}`);
            const data = await response.json();

            if (response.ok && data.success) {
                const leads = (data.leads || []).map(normalizeLeadForDisplay);
                setResults(leads);
                // Expandir el primer lead por defecto
                if (leads.length > 0) {
                    setExpandedLeads({ [leads[0].id]: true });
                }
                // Cargar campañas/attachments/contrato en segundo plano, por lead,
                // sin esperar a que termine para no retrasar la lista ya visible.
                leads.forEach(lead => { fetchLeadExtra(lead); });
            } else {
                setError(data.message || 'No se encontraron leads');
            }
        } catch (err) {
            console.error('Error en búsqueda:', err);
            setError('Error al buscar en Zoho CRM');
        } finally {
            setLoading(false);
        }
    }, [authFetch, fetchLeadExtra]);

    const handleSearch = () => {
        if (!isValidInput()) return;
        runSearch(searchType, searchValue);
    };

    useEffect(() => {
        if (!leadSearchRequest?.phone) return;

        setSearchType('phone');
        setSearchValue(leadSearchRequest.phone);
        runSearch('phone', leadSearchRequest.phone);
    }, [leadSearchRequest, runSearch]);

    const toggleLeadExpansion = (leadId) => {
        setExpandedLeads(prev => ({
            ...prev,
            [leadId]: !prev[leadId]
        }));
    };

    const handleDownloadAttachment = async (attachment, leadId) => {
        try {
            const response = await authFetch(`/api/zoho/attachments/${attachment.id}/download?lead_id=${leadId}`);
            if (!response.ok) throw new Error('Error al descargar');
            
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = attachment.file_name || `attachment_${attachment.id}`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Error en descarga:', error);
            alert('No se pudo descargar el archivo.');
        }
    };

    const handleClear = () => {
        setSearchValue('');
        setResults([]);
        setError(null);
        setExpandedLeads({});
    };

    const formatSize = (bytes) => {
        if (!bytes) return 'N/A';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        try {
            return new Date(dateStr).toLocaleString('es-ES', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return dateStr;
        }
    };

    const getFileIcon = (fileName) => {
        if (!fileName) return File;
        const ext = fileName.split('.').pop()?.toLowerCase();
        if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return Image;
        if (['pdf', 'doc', 'docx', 'txt'].includes(ext)) return FileText;
        return File;
    };

    const getInputBorderColor = () => {
        if (!searchValue.trim()) return 'border-slate-700';
        return isValidInput() ? 'border-emerald-500' : 'border-red-500';
    };

    return (
        <AppendixWrapper 
            id={id} 
            title="Búsqueda de Leads" 
            icon={Search} 
            className="w-[900px] h-[70vh] max-h-[700px]"
        >
            <div className="flex flex-col h-full bg-slate-950">
                {/* Formulario de búsqueda */}
                <div className="p-4 border-b border-slate-800 bg-slate-900/50">
                    {/* Radio buttons para tipo de búsqueda */}
                    <div className="flex gap-4 mb-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="radio"
                                value="phone"
                                checked={searchType === 'phone'}
                                onChange={(e) => setSearchType(e.target.value)}
                                className="text-primary focus:ring-primary"
                            />
                            <span className="text-xs text-slate-300">Teléfono</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="radio"
                                value="email"
                                checked={searchType === 'email'}
                                onChange={(e) => setSearchType(e.target.value)}
                                className="text-primary focus:ring-primary"
                            />
                            <span className="text-xs text-slate-300">Email</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="radio"
                                value="cups"
                                checked={searchType === 'cups'}
                                onChange={(e) => setSearchType(e.target.value)}
                                className="text-primary focus:ring-primary"
                            />
                            <span className="text-xs text-slate-300">CUPS</span>
                        </label>
                    </div>

                    {/* Input de búsqueda */}
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && isValidInput() && handleSearch()}
                            placeholder={
                                searchType === 'phone' ? '600123456' :
                                searchType === 'email' ? 'email@ejemplo.com' :
                                'ES1234567890123456TF'
                            }
                            className={`flex-1 px-3 py-2 bg-slate-800 border ${getInputBorderColor()} rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary transition-colors`}
                        />
                        <button
                            onClick={handleSearch}
                            disabled={!isValidInput() || loading}
                            className="px-4 py-2 bg-primary hover:bg-primary/80 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
                        >
                            {loading ? <Loader2 size={16} className="animate-spin" /> : 'Buscar'}
                        </button>
                        {(results.length > 0 || error) && (
                            <button
                                onClick={handleClear}
                                className="p-2 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors"
                                title="Limpiar"
                            >
                                <X size={16} />
                            </button>
                        )}
                    </div>

                    {searchValue.trim() && !isValidInput() && (
                        <p className="text-xs text-red-400 mt-2">
                            {searchType === 'phone' && 'Formato inválido. Ej: 600123456'}
                            {searchType === 'email' && 'Formato inválido. Ej: email@ejemplo.com'}
                            {searchType === 'cups' && 'Formato inválido. Ej: ES1234567890123456TF'}
                        </p>
                    )}
                </div>

                {/* Resultados */}
                <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
                    {loading && (
                        <div className="flex flex-col items-center justify-center py-12">
                            <Loader2 size={32} className="text-primary animate-spin mb-3" />
                            <p className="text-sm text-slate-400">Buscando lead...</p>
                        </div>
                    )}

                    {error && (
                        <div className="flex items-start gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                            <AlertCircle size={18} className="text-red-400 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="text-sm text-red-300 font-medium">No se encontraron leads</p>
                                <p className="text-xs text-red-400 mt-1">{error}</p>
                            </div>
                        </div>
                    )}

                    {/* Contador de resultados */}
                    {results.length > 0 && (
                        <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                            <p className="text-sm text-emerald-300">
                                Se encontraron <span className="font-bold">{results.length}</span> lead{results.length !== 1 ? 's' : ''}
                            </p>
                        </div>
                    )}

                    {/* Acordeones de leads */}
                    {results.length > 0 && (
                        <div className="space-y-3">
                            {results.map((lead, index) => {
                                const isExpanded = expandedLeads[lead.id];
                                
                                return (
                                    <div key={lead.id} className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
                                        {/* Header del acordeón - Datos básicos */}
                                        <button
                                            onClick={() => toggleLeadExpansion(lead.id)}
                                            className="w-full p-4 flex items-center justify-between hover:bg-slate-800/50 transition-colors"
                                        >
                                            <div className="flex items-center gap-4 flex-1 text-left">
                                                <div className="flex items-center justify-center w-8 h-8 bg-primary/20 text-primary rounded-full font-bold text-sm">
                                                    {index + 1}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-3 mb-1">
                                                        <h4 className="text-base font-bold text-slate-100 truncate">
                                                            {lead.full_name || 'Sin nombre'}
                                                        </h4>
                                                        {lead.visitor_score && (
                                                            <span className="px-2 py-0.5 bg-amber-500/20 text-amber-300 text-xs rounded-full">
                                                                Score: {lead.visitor_score}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-3 text-xs text-slate-400">
                                                        {lead.phone && <span>📞 {lead.phone}</span>}
                                                        {lead.email && <span>✉️ {lead.email}</span>}
                                                        {lead.created_time && (
                                                            <span>📅 {formatDate(lead.created_time)}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            {isExpanded ? <ChevronUp size={20} className="text-slate-400 flex-shrink-0" /> : <ChevronDown size={20} className="text-slate-400 flex-shrink-0" />}
                                        </button>

                                        {/* Contenido expandido - Detalles completos */}
                                        {isExpanded && (
                                            <div className="border-t border-slate-800 p-4 bg-slate-900/50">
                                                <div className="grid grid-cols-2 gap-3">
                                                    {/* Información básica */}
                                                    <div className="p-3 bg-slate-800/50 border border-slate-700 rounded-lg">
                                                        <div className="flex items-center gap-2 mb-3">
                                                            <User size={14} className="text-primary" />
                                                            <h5 className="text-xs font-bold text-slate-100 uppercase tracking-wider">Información Básica</h5>
                                                        </div>
                                                        <div className="space-y-2">
                                                            {lead.dni && (
                                                                <div>
                                                                    <p className="text-xs text-slate-500">DNI</p>
                                                                    <p className="text-sm text-slate-200">{lead.dni}</p>
                                                                </div>
                                                            )}
                                                            {lead.perfil && (
                                                                <div>
                                                                    <p className="text-xs text-slate-500">Perfil</p>
                                                                    <p className="text-sm text-slate-200">{lead.perfil}</p>
                                                                </div>
                                                            )}
                                                            {lead.external_id && (
                                                                <div>
                                                                    <p className="text-xs text-slate-500">External ID</p>
                                                                    <p className="text-sm text-slate-200">{lead.external_id}</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Dirección */}
                                                    {(lead.address || lead.city) && (
                                                        <div className="p-3 bg-slate-800/50 border border-slate-700 rounded-lg">
                                                            <div className="flex items-center gap-2 mb-3">
                                                                <MapPin size={14} className="text-primary" />
                                                                <h5 className="text-xs font-bold text-slate-100 uppercase tracking-wider">Dirección</h5>
                                                            </div>
                                                            <div className="space-y-2">
                                                                {lead.address && <p className="text-sm text-slate-200">{lead.address}</p>}
                                                                <p className="text-sm text-slate-300">
                                                                    {[lead.city, lead.province, lead.zip_code].filter(Boolean).join(', ')}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* CUPS y Tarifa */}
                                                    {(lead.cups || lead.tarifa || lead.tarifa_precio || lead.ahorro_mensual || lead.estimacion_facturacion_mensual || lead.tarifa_atr || lead.tipo_tarifa_atr || lead.ahorro_anual || lead.resultado_comparador || lead.comercializadora_anterior) && (
                                                        <div className="col-span-2 p-3 bg-slate-800/50 border border-slate-700 rounded-lg">
                                                            <div className="flex items-center gap-2 mb-3">
                                                                <Zap size={14} className="text-primary" />
                                                                <h5 className="text-xs font-bold text-slate-100 uppercase tracking-wider">Suministro y Tarifas</h5>
                                                            </div>
                                                            <div className="grid grid-cols-2 gap-4">
                                                                {lead.cups && (
                                                                    <div>
                                                                        <p className="text-xs text-slate-500">CUPS</p>
                                                                        <p className="text-sm text-slate-200 font-mono">{lead.cups}</p>
                                                                    </div>
                                                                )}
                                                                {lead.comercializadora_anterior && (
                                                                    <div>
                                                                        <p className="text-xs text-slate-500">Comercializadora Origen</p>
                                                                        <p className="text-sm text-slate-200">{lead.comercializadora_anterior}</p>
                                                                    </div>
                                                                )}
                                                                {lead.tarifa && (
                                                                    <div>
                                                                        <p className="text-xs text-slate-500">Tarifa</p>
                                                                        <p className="text-sm text-slate-200">{lead.tarifa}</p>
                                                                    </div>
                                                                )}
                                                                {lead.tarifa_atr && (
                                                                    <div>
                                                                        <p className="text-xs text-slate-500">Tarifa ATR</p>
                                                                        <p className="text-sm text-slate-200">{lead.tarifa_atr}</p>
                                                                    </div>
                                                                )}
                                                                {lead.tipo_tarifa_atr && (
                                                                    <div>
                                                                        <p className="text-xs text-slate-500">Tipo Tarifa ATR</p>
                                                                        <p className="text-sm text-slate-200">{lead.tipo_tarifa_atr}</p>
                                                                    </div>
                                                                )}
                                                                {lead.tarifa_precio && (
                                                                    <div>
                                                                        <p className="text-xs text-slate-500">Precio Tarifa</p>
                                                                        <p className="text-sm text-slate-200">{lead.tarifa_precio}</p>
                                                                    </div>
                                                                )}
                                                                {lead.ahorro_mensual && (
                                                                    <div>
                                                                        <p className="text-xs text-slate-500">Ahorro Mensual</p>
                                                                        <p className="text-sm text-slate-200">{lead.ahorro_mensual}</p>
                                                                    </div>
                                                                )}
                                                                {lead.ahorro_anual && (
                                                                    <div>
                                                                        <p className="text-xs text-slate-500">Ahorro Anual</p>
                                                                        <p className="text-sm text-slate-200">{lead.ahorro_anual}</p>
                                                                    </div>
                                                                )}
                                                                {lead.estimacion_facturacion_mensual && (
                                                                    <div>
                                                                        <p className="text-xs text-slate-500">Estimación Facturación Mensual</p>
                                                                        <p className="text-sm text-slate-200">{lead.estimacion_facturacion_mensual}</p>
                                                                    </div>
                                                                )}
                                                                {lead.resultado_comparador && (
                                                                    <div>
                                                                        <p className="text-xs text-slate-500">Resultado Comparador</p>
                                                                        <p className="text-sm text-slate-200">{lead.resultado_comparador}</p>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Origen */}
                                                    {(lead.source || lead.aglutinator || lead.origen_it) && (
                                                        <div className="p-3 bg-slate-800/50 border border-slate-700 rounded-lg">
                                                            <div className="flex items-center gap-2 mb-3">
                                                                <Tag size={14} className="text-primary" />
                                                                <h5 className="text-xs font-bold text-slate-100 uppercase tracking-wider">Origen</h5>
                                                            </div>
                                                            <div className="space-y-2">
                                                                {lead.source && (
                                                                    <div>
                                                                        <p className="text-xs text-slate-500">Lead Source</p>
                                                                        <p className="text-sm text-slate-200">{lead.source}</p>
                                                                    </div>
                                                                )}
                                                                {lead.aglutinator && (
                                                                    <div>
                                                                        <p className="text-xs text-slate-500">Aglutinador</p>
                                                                        <p className="text-sm text-slate-200">{lead.aglutinator}</p>
                                                                    </div>
                                                                )}
                                                                {lead.origen_it && (
                                                                    <div>
                                                                        <p className="text-xs text-slate-500">Origen IT</p>
                                                                        <p className="text-sm text-slate-200">{lead.origen_it}</p>
                                                                    </div>
                                                                )}
                                                                {lead.interes_original && (
                                                                    <div>
                                                                        <p className="text-xs text-slate-500">Interés Original</p>
                                                                        <p className="text-sm text-slate-200">{lead.interes_original}</p>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Campañas, contrato y attachments: segundo paso, cargados en diferido */}
                                                    {(() => {
                                                        const extra = leadExtras[lead.id];
                                                        if (extra?.loading) {
                                                            return (
                                                                <div className="col-span-2 flex items-center gap-2 p-3 text-xs text-slate-400">
                                                                    <Loader2 size={14} className="animate-spin" />
                                                                    Cargando campañas y contrato vinculado...
                                                                </div>
                                                            );
                                                        }
                                                        if (extra?.error) {
                                                            return (
                                                                <div className="col-span-2 p-3 text-xs text-red-400">
                                                                    {extra.error}
                                                                </div>
                                                            );
                                                        }
                                                        return null;
                                                    })()}

                                                    {/* Contrato vinculado */}
                                                    {leadExtras[lead.id] && !leadExtras[lead.id].loading && !leadExtras[lead.id].error && (
                                                        <div className="col-span-2 p-3 bg-slate-800/50 border border-slate-700 rounded-lg">
                                                            <div className="flex items-center gap-2 mb-3">
                                                                <FileSignature size={14} className="text-primary" />
                                                                <h5 className="text-xs font-bold text-slate-100 uppercase tracking-wider">Contrato Vinculado</h5>
                                                            </div>
                                                            {leadExtras[lead.id].contractInfo?.has_contract ? (
                                                                <div className="space-y-2">
                                                                    <div className="flex items-center gap-2 text-emerald-300 text-sm">
                                                                        <CheckCircle2 size={16} />
                                                                        Sí, {leadExtras[lead.id].contractInfo.contracts.length} contrato{leadExtras[lead.id].contractInfo.contracts.length !== 1 ? 's' : ''} vinculado{leadExtras[lead.id].contractInfo.contracts.length !== 1 ? 's' : ''}
                                                                    </div>
                                                                    <div className="grid grid-cols-2 gap-2">
                                                                        {leadExtras[lead.id].contractInfo.contracts.map((contract) => (
                                                                            <div key={contract.id} className="p-2 bg-slate-700/50 rounded text-xs text-slate-200">
                                                                                <p className="font-medium">{contract.contract_number || contract.id}</p>
                                                                                <p className="text-slate-400">
                                                                                    {[contract.status, contract.contract_type].filter(Boolean).join(' · ')}
                                                                                </p>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div className="flex items-center gap-2 text-slate-400 text-sm">
                                                                    <XCircle size={16} />
                                                                    Sin contrato vinculado
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* Campañas */}
                                                    {leadExtras[lead.id]?.campaigns && leadExtras[lead.id].campaigns.length > 0 && (
                                                        <div className="col-span-2 p-3 bg-slate-800/50 border border-slate-700 rounded-lg">
                                                            <div className="flex items-center gap-2 mb-3">
                                                                <Tag size={14} className="text-primary" />
                                                                <h5 className="text-xs font-bold text-slate-100 uppercase tracking-wider">
                                                                    Campañas ({leadExtras[lead.id].campaigns.length})
                                                                </h5>
                                                            </div>
                                                            <div className="grid grid-cols-2 gap-2">
                                                                {leadExtras[lead.id].campaigns.map((campaign, idx) => (
                                                                    <div key={idx} className="p-2 bg-slate-700/50 rounded text-xs text-slate-200">
                                                                        {campaign}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Attachments */}
                                                    {leadExtras[lead.id]?.attachments && leadExtras[lead.id].attachments.length > 0 && (
                                                        <div className="col-span-2 p-3 bg-slate-800/50 border border-slate-700 rounded-lg">
                                                            <div className="flex items-center gap-2 mb-3">
                                                                <FileText size={14} className="text-primary" />
                                                                <h5 className="text-xs font-bold text-slate-100 uppercase tracking-wider">
                                                                    Archivos Adjuntos ({leadExtras[lead.id].attachments.length})
                                                                </h5>
                                                            </div>
                                                            <div className="grid grid-cols-2 gap-2">
                                                                {leadExtras[lead.id].attachments.map((att) => {
                                                                    const Icon = getFileIcon(att.file_name);
                                                                    return (
                                                                        <div key={att.id} className="p-2 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors">
                                                                            <div className="flex items-start gap-2">
                                                                                <div className="p-1.5 bg-slate-600 rounded flex-shrink-0">
                                                                                    <Icon size={16} className="text-slate-300" />
                                                                                </div>
                                                                                <div className="flex-1 min-w-0">
                                                                                    <p className="text-xs text-slate-200 font-medium truncate">
                                                                                        {att.file_name || 'Sin nombre'}
                                                                                    </p>
                                                                                    <div className="flex items-center gap-2 mt-0.5">
                                                                                        <span className="text-xs text-slate-400">{formatSize(att.size)}</span>
                                                                                    </div>
                                                                                </div>
                                                                                <button
                                                                                    onClick={() => handleDownloadAttachment(att, lead.id)}
                                                                                    className="p-1.5 hover:bg-slate-600 rounded text-slate-400 hover:text-primary transition-colors flex-shrink-0"
                                                                                    title="Descargar"
                                                                                >
                                                                                    <Download size={14} />
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {!loading && !error && results.length === 0 && (
                        <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                            <Search size={48} className="mb-3 opacity-50" />
                            <p className="text-sm">Realiza una búsqueda para ver resultados</p>
                        </div>
                    )}
                </div>
            </div>
        </AppendixWrapper>
    );
};

export default LeadDeepSearchAppendix;
